import React, { useState, useEffect } from 'react';
import { Layout, Tree, Button, Modal, Form, Select, message, Input, Typography, Flex } from 'antd';
import type { TreeProps } from 'antd/es/tree';
import { PlusOutlined, FolderOutlined, FileOutlined, DownOutlined, ReloadOutlined } from '@ant-design/icons';
import MetadataEditor from './MetadataEditor';
import ResizablePanel from '../ResizablePanel';
import { API_BASE_URL } from '../../config';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('MetadataView');
import { useTranslation } from 'react-i18next';
import { metadataService, MetadataType, MetadataItem, CreateMetadataRequest, ApiError } from '../../api';
import useTabManagerStore from '../../store/tabManagerStore';
import { useMarkdownActions } from '../../store/store';

const { Search } = Input;
const { Title } = Typography;

// Type definitions
interface MetadataViewProps {
  style?: React.CSSProperties;
}

interface TreeNodeData {
  title: string;
  key: string;
  icon: React.ReactNode;
  isLeaf: boolean;
  children?: TreeNodeData[];
}

interface ApiMetadataType {
  name: string;
  key: string;
  items?: MetadataItem[];
}

interface FormValues {
  name: string;
  type: string;
}

const MetadataView: React.FC<MetadataViewProps> = ({ style }) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredTreeData, setFilteredTreeData] = useState<TreeNodeData[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>(() => {
    const stored = localStorage.getItem('metadata_expandedKeys');
    return stored ? JSON.parse(stored) : [];
  });
  const [form] = Form.useForm<FormValues>();
  const [apiMetadataTypes, setApiMetadataTypes] = useState<MetadataType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
  const [treeLoading, setTreeLoading] = useState<boolean>(false);
  const { openTab, updateTabModified } = useTabManagerStore((state) => ({
    openTab: state.openTab,
    updateTabModified: state.updateTabModified,
  }));
  const { addMarkdown } = useMarkdownActions();

  // Fetch tree data using the new API service
  const fetchTreeData = async (): Promise<void> => {
    setTreeLoading(true);
    try {
      const data = await metadataService.getMetadata();
      logger.debug('Raw metadata data from API:', data);
      
      const transformedData: TreeNodeData[] = data
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((type) => ({
          title: type.name,
          key: type.key,
          icon: <FolderOutlined />,
          isLeaf: false,
          children: type.items
            ? type.items
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => ({
                  title: item.name,
                  key: `${type.key}/${item.key}`,
                  icon: <FileOutlined />,
                  isLeaf: true,
                }))
            : [],
        }));
      
      logger.debug('Transformed tree data:', transformedData);
      setTreeData(transformedData);
      setFilteredTreeData(transformedData);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to load metadata items';
      logger.error('Error fetching metadata items:', error);
      message.error(errorMessage);
    } finally {
      setTreeLoading(false);
    }
  };

  // Initial fetch of tree data
  useEffect(() => {
    fetchTreeData();
  }, []);

  // Save expanded keys to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('metadata_expandedKeys', JSON.stringify(expandedKeys));
  }, [expandedKeys]);

  // Update filtered tree data whenever treeData or searchTerm changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredTreeData(treeData);
      return;
    }

    const filterTree = (nodes: TreeNodeData[], term: string): TreeNodeData[] => {
      const lowerCaseTerm = term.toLowerCase();

      return nodes
        .map((node) => {
          // Check if this node matches
          const nodeMatches = node.title.toLowerCase().includes(lowerCaseTerm);

          // Check if any children match
          let filteredChildren: TreeNodeData[] = [];
          if (node.children) {
            filteredChildren = filterTree(node.children, term);
          }

          // Keep this node if it matches or has matching children
          if (nodeMatches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }

          return null;
        })
        .filter(Boolean) as TreeNodeData[];
    };

    setFilteredTreeData(filterTree(treeData, searchTerm));

    // Expand all nodes when searching
    if (searchTerm) {
      const allKeys = treeData.map((node) => node.key);
      setExpandedKeys(allKeys);
    }
  }, [treeData, searchTerm]);

  const handleAddClick = async (): Promise<void> => {
    setIsAddModalVisible(true);
    setLoading(true);
    try {
      const data = await metadataService.getMetadataTypes();
      setApiMetadataTypes(data);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to load metadata types';
      logger.error('Error fetching metadata types:', error);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddModalOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const request: CreateMetadataRequest = {
          key: values.type,
          name: values.name,
      };

      await metadataService.createMetadataItem(request);

      message.success('Metadata item created successfully');
      setIsAddModalVisible(false);
      form.resetFields();

      // Refresh the tree data
      await fetchTreeData();
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Failed to create metadata item';
      logger.error('Failed to create item:', error);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect: TreeProps['onSelect'] = async (keys, info) => {
    if (keys.length > 0) {
      const node = info.node;
      // Only set selection if it's a leaf node (metadata item)
      if (node.isLeaf) {
        const [type, itemId] = (keys[0] as string).split('/');
        // Comment out setting selected type and item for editing
        // setSelectedType(type);
        // setSelectedItem(itemId);

        try {
          // Fetch metadata content using the service
          const content = await metadataService.getMetadataContent(type, itemId);
          
          // Convert content to a formatted string if it's an object
          let formattedContent: string;
          if (typeof content === 'object' && content !== null) {
            formattedContent = JSON.stringify(content, null, 2);
          } else {
            formattedContent = String(content || '# No content available');
          }

          // Create a new tab with the content
          const tabId = `metadata_${type}_${itemId}`;
          const title = typeof node.title === 'string' ? node.title : String(node.title);
          
          // First add the markdown content to the store with proper initialization
          addMarkdown(tabId, title, formattedContent);
          
          // Then create the tab (existing file from metadata, so isNew = false)
          openTab(tabId, 'markdown', title, undefined, undefined, false);
          
          // Mark existing metadata files as NOT modified since they're being opened from storage
          updateTabModified(tabId, false);
          
          // Hide welcome overlay if it's showing
          window.dispatchEvent(new CustomEvent('hideWelcomeOverlay'));

          logger.debug('Opening metadata item:', {
            name: title,
            type,
            itemId,
            tabId,
          });
        } catch (error) {
          const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch metadata content';
          logger.error('Error fetching metadata content:', error);
          message.error(errorMessage);
        }
      }
    }
  };

  const typeOptions = apiMetadataTypes
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((type) => ({
      label: type.name,
      value: type.key,
    }));

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleModalCancel = (): void => {
    setIsAddModalVisible(false);
    form.resetFields();
  };

  const handleTreeClick: TreeProps['onClick'] = (e, node) => {
    // If clicking on a non-leaf node, toggle its expansion
    if (!node.isLeaf) {
      const key = node.key;
      setExpandedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchTreeData();
  };

  return (
    <ResizablePanel>
      <Flex vertical style={{ width: '100%', height: '100%' }}>
        <Flex
          vertical
          gap="small"
          style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}
        >
          <Flex justify="space-between" align="center">
            <Title level={4} style={{ margin: 0 }}>
              {t('metadata.title')}
            </Title>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={handleRefresh}
              loading={treeLoading}
            />
          </Flex>
          <Search
            placeholder={t('metadata.search')}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: '100%' }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ width: '100%' }}
            onClick={handleAddClick}
            loading={loading}
          >
            {t('metadata.addNewItem')}
          </Button>
        </Flex>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          {treeLoading ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>{t('metadata.loading')}</div>
          ) : filteredTreeData.length > 0 ? (
            <Tree
              showIcon
              treeData={filteredTreeData}
              onSelect={handleSelect}
              expandedKeys={expandedKeys}
              switcherIcon={<DownOutlined />}
              onExpand={setExpandedKeys}
              onClick={handleTreeClick}
            />
          ) : (
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#999' }}>
              {t('metadata.noItems')}
            </div>
          )}
        </div>

        <Modal
          title={t('metadata.addModalTitle')}
          open={isAddModalVisible}
          onOk={handleAddModalOk}
          onCancel={handleModalCancel}
          confirmLoading={loading}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="type"
              label={t('metadata.type')}
              rules={[{ required: true, message: t('metadata.typeRequired') }]}
            >
              <Select
                placeholder={t('metadata.typePlaceholder')}
                options={typeOptions}
                loading={loading}
                disabled={loading}
              />
            </Form.Item>
            <Form.Item
              name="name"
              label={t('metadata.name')}
              rules={[{ required: true, message: t('metadata.nameRequired') }]}
            >
              <Input placeholder={t('metadata.namePlaceholder')} />
            </Form.Item>
          </Form>
        </Modal>

        {selectedItem && (
          <Modal
            title={t('metadata.editModalTitle')}
            open={!!selectedItem}
            footer={null}
            onCancel={() => setSelectedItem(null)}
          >
            <MetadataEditor itemId={selectedItem} />
          </Modal>
        )}
      </Flex>
    </ResizablePanel>
  );
};

export default MetadataView;
